import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  Droplets,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  ChevronUp
} from "lucide-react";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/about", label: "About Us" },
    { path: "/service-areas", label: "Service Areas" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2 hidden md:block">
        <div className="container-custom flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+1-888-435-4674" className="flex items-center gap-2 hover:text-sky-400 transition-colors" data-testid="top-phone">
              <Phone className="w-4 h-4" />
              <span>+1-888-435-4674</span>
            </a>
            <a href="mailto:support@buyvistas.store" className="flex items-center gap-2 hover:text-sky-400 transition-colors" data-testid="top-email">
              <Mail className="w-4 h-4" />
              <span>support@buyvistas.store</span>
            </a>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>20 E 22nd St #4699 New York, NY 10165</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header 
        className={`sticky top-0 z-50 w-full backdrop-blur-xl bg-white/95 border-b border-slate-200 transition-shadow duration-300 ${isScrolled ? "shadow-lg" : ""}`}
        data-testid="main-header"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-slate-900 rounded-sm flex items-center justify-center">
                <Droplets className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-extrabold tracking-tight text-slate-900 uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                BuyVistas
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-semibold uppercase tracking-wide text-sm transition-colors ${
                    isActive(link.path) 
                      ? "text-sky-600" 
                      : "text-slate-700 hover:text-sky-600"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href="tel:+1-888-435-4674" 
                className="flex items-center gap-2 text-slate-700 hover:text-sky-600 transition-colors font-semibold"
                data-testid="header-call-btn"
              >
                <Phone className="w-5 h-5" />
                <span className="hidden xl:inline">Call Now</span>
              </a>
              <Link 
                to="/contact" 
                className="btn-primary"
                data-testid="header-quote-btn"
              >
                <span>Get Quote</span>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-slate-200"
              data-testid="mobile-menu"
            >
              <div className="container-custom py-4">
                <nav className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`font-semibold uppercase tracking-wide py-2 ${
                        isActive(link.path) ? "text-sky-600" : "text-slate-700"
                      }`}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="flex flex-col gap-3 pt-4 border-t border-slate-200">
                    <a 
                      href="tel:+1-888-435-4674" 
                      className="flex items-center gap-2 text-slate-700 font-semibold"
                    >
                      <Phone className="w-5 h-5" />
                      +1-888-435-4674
                    </a>
                    <Link to="/contact" className="btn-primary text-center">
                      <span>Get Free Quote</span>
                    </Link>
                  </div>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export const Footer = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const quickLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/about", label: "About Us" },
    { path: "/service-areas", label: "Service Areas" },
    { path: "/contact", label: "Contact" },
  ];

  const serviceLinks = [
    { path: "/services#house-washing", label: "House Washing" },
    { path: "/services#driveway-cleaning", label: "Driveway Cleaning" },
    { path: "/services#deck-patio", label: "Deck & Patio Cleaning" },
    { path: "/services#roof-cleaning", label: "Roof Cleaning" },
    { path: "/services#commercial", label: "Commercial Washing" },
    { path: "/services#sidewalk", label: "Sidewalk Cleaning" },
  ];

  const policyLinks = [
    { path: "/privacy-policy", label: "Privacy Policy" },
    { path: "/terms-conditions", label: "Terms & Conditions" },
    { path: "/disclaimer", label: "Disclaimer" },
    { path: "/cookie-policy", label: "Cookie Policy" },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-sky-700 rounded-sm flex items-center justify-center">
                <Droplets className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-extrabold tracking-tight uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                BuyVistas
              </span>
            </Link>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Professional pressure washing services for residential and commercial properties. 
              We restore the beauty of your property with expert cleaning solutions.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" data-testid="social-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" data-testid="social-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" data-testid="social-twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" data-testid="social-linkedin">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold uppercase tracking-wider mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-slate-400 hover:text-sky-400 transition-colors"
                    data-testid={`footer-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold uppercase tracking-wider mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Our Services
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-slate-400 hover:text-sky-400 transition-colors"
                    data-testid={`footer-service-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold uppercase tracking-wider mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-sky-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-slate-400">Phone</p>
                  <a href="tel:+1-888-435-4674" className="text-white hover:text-sky-400 transition-colors font-semibold" data-testid="footer-phone">
                    +1-888-435-4674
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-sky-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-slate-400">Email</p>
                  <a href="mailto:support@buyvistas.store" className="text-white hover:text-sky-400 transition-colors font-semibold" data-testid="footer-email">
                    support@buyvistas.store
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-sky-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-slate-400">Address</p>
                  <p className="text-white">20 E 22nd St #4699<br />New York, NY 10165</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-slate-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} BuyVistas. All rights reserved.
            </p>
            <ul className="flex flex-wrap justify-center gap-4 text-sm">
              {policyLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-slate-400 hover:text-sky-400 transition-colors"
                    data-testid={`footer-policy-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center shadow-lg transition-colors z-40"
          data-testid="scroll-to-top-btn"
        >
          <ChevronUp className="w-6 h-6" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default function Layout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow">{children}</main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
